// src/lib/sat-domains.ts
// Reference data for the 8 official Digital SAT content domains:
//   - The mapping from each topic string used in our question bank
//     to its parent domain.
//   - The approximate number of questions per full Digital SAT
//     (98-question form) within each domain.
//
// Sources:
//   - College Board "Assessment Framework for the SAT Suite" (2024)
//   - "Digital SAT Bluebook" sample content distribution
//
// Usage:
//   - Radar chart axes (one axis per domain, accuracy aggregated up).
//   - Topic heatmap grouping (cells grouped by domain).
//   - Leverage scoring (domain question count is the frequency weight).
//   - Topic display canonicalization (some topic strings in our seed
//     are duplicates with slightly different wording — see canonicalTopic
//     below).
//
// HOW domainForTopic RESOLVES (in order):
//   1) Canonical taxonomy from src/lib/sat/taxonomy.ts — the single
//      source of truth defined by the #368 migration. Covers the 30
//      Title Case canonical topic names.
//   2) Legacy TOPIC_TO_DOMAIN map below — for any pre-migration topic
//      strings still in old code paths or archived rows.
//   3) "Other" — true unknowns. Surface as a catch-all column rather
//      than silently dropping data.
//
// Adding a new canonical topic? Update src/lib/sat/taxonomy.ts; this
// file picks it up automatically. The legacy TOPIC_TO_DOMAIN map
// is frozen — only add to it if a pre-migration topic string surfaces
// in an old row.

import { domainForTopic as canonicalDomainForTopic, SAT_TAXONOMY } from '@/lib/sat/taxonomy'

export type SatDomain =
  // Reading & Writing
  | 'Information and Ideas'
  | 'Craft and Structure'
  | 'Expression of Ideas'
  | 'Standard English Conventions'
  // Math
  | 'Algebra'
  | 'Advanced Math'
  | 'Problem-Solving and Data Analysis'
  | 'Geometry and Trigonometry'
  // Catch-all for topic strings we haven't mapped yet
  | 'Other'

export type SatSection = 'RW' | 'MATH'

/**
 * The 8 official domains, in canonical display order. Used for the radar
 * chart axes and any domain-grouped UI. "Other" is excluded from this
 * list — it's a runtime catch-all, not a domain users should aim at.
 */
export const SAT_DOMAINS: readonly SatDomain[] = [
  'Information and Ideas',
  'Craft and Structure',
  'Expression of Ideas',
  'Standard English Conventions',
  'Algebra',
  'Advanced Math',
  'Problem-Solving and Data Analysis',
  'Geometry and Trigonometry',
] as const

/**
 * Which section of the test each domain belongs to. Used for color-coding
 * (RW = cyan, Math = violet) and to filter when only one section matters.
 */
export const DOMAIN_SECTION: Record<SatDomain, SatSection | null> = {
  'Information and Ideas': 'RW',
  'Craft and Structure': 'RW',
  'Expression of Ideas': 'RW',
  'Standard English Conventions': 'RW',
  Algebra: 'MATH',
  'Advanced Math': 'MATH',
  'Problem-Solving and Data Analysis': 'MATH',
  'Geometry and Trigonometry': 'MATH',
  Other: null,
}

/**
 * Approximate number of questions on a full Digital SAT (98 q total) that
 * fall within each domain. Used as the "frequency weight" in the leverage
 * score: closing a gap on a high-frequency domain is worth more raw points
 * on test day than the same gap on a low-frequency domain.
 *
 * RW total: 54 (13 + 14 + 13 + 14). Math total: 44 (15 + 15 + 7 + 7).
 * Numbers are integers chosen to (a) sum to the official totals and
 * (b) match College Board's published "approximately N percent" ranges.
 *
 * THE MATH NUMBERS WERE WRONG. They read 13/13/9/9, which puts Problem-Solving
 * and Data Analysis and Geometry and Trigonometry at 20.5% each against a
 * published ~15%, and Algebra and Advanced Math at 29.5% against ~35%. The
 * comment above claimed they matched the published ranges; they did not, and
 * the error reached the benchmark blueprint as a module of 7/7/4/4 — a
 * 44-question path of 14/14/8/8 when College Board publishes 13–15 / 13–15 /
 * 5–7 / 5–7. Two of the four domains sat outside the specification.
 *
 * This constant is also the frequency weight in topic leverage and score
 * projection, so correcting it moves those: Algebra and Advanced Math topics
 * gain weight, Problem-Solving and Geometry topics lose it. That is the point —
 * the weight is meant to say how many test-day questions a topic is worth.
 */
export const DOMAIN_QUESTIONS_PER_TEST: Record<SatDomain, number> = {
  // Reading & Writing — 54 total
  'Information and Ideas': 13,
  'Craft and Structure': 14,
  'Expression of Ideas': 13,
  'Standard English Conventions': 14,
  // Math — 44 total
  Algebra: 15,
  'Advanced Math': 15,
  'Problem-Solving and Data Analysis': 7,
  'Geometry and Trigonometry': 7,
  // Unknown/unmapped — leverage weight 0 so they don't pollute rankings
  Other: 0,
}

/**
 * Number of canonical taxonomy topics in each domain, derived from
 * SAT_TAXONOMY at module load so it can NEVER drift from the taxonomy
 * (add/split a topic and this updates automatically). Used to split a
 * domain's per-test question count evenly across its topics — see
 * `questionsPerTestForTopic`.
 *
 * "Coordinate Geometry" (UnlimitedTests-only) IS counted in Geometry's
 * total: the weak-topic SQL groups by the raw `Question.topic` string, so a
 * "Coordinate Geometry" row can surface on the study plan and needs a
 * weight, and counting all of a domain's taxonomy topics keeps the
 * invariant that the per-topic weights sum back to the domain's count.
 *
 * "Other" has no canonical topics (0); division is guarded before use.
 */
export const CANONICAL_TOPIC_COUNT: Record<SatDomain, number> = (() => {
  const counts: Record<SatDomain, number> = {
    'Information and Ideas': 0,
    'Craft and Structure': 0,
    'Expression of Ideas': 0,
    'Standard English Conventions': 0,
    Algebra: 0,
    'Advanced Math': 0,
    'Problem-Solving and Data Analysis': 0,
    'Geometry and Trigonometry': 0,
    Other: 0,
  }
  for (const spec of SAT_TAXONOMY) {
    counts[spec.domain] += spec.topics.length
  }
  return counts
})()

/**
 * Map every topic string that appears in our question bank (as of the
 * most recent seed) to its parent domain. Confirmed against the user's
 * mapping table on 2026-04-29.
 *
 * If a topic is missing here, `domainForTopic` returns "Other" — that
 * surfaces the gap on the heatmap rather than silently dropping the data.
 */
export const TOPIC_TO_DOMAIN: Record<string, SatDomain> = {
  // Reading & Writing — Information and Ideas
  'Central Ideas and Details': 'Information and Ideas',
  'Command of Evidence': 'Information and Ideas',
  Inferences: 'Information and Ideas',
  // Reading & Writing — Craft and Structure
  'Words in Context': 'Craft and Structure',
  'Text Structure and Purpose': 'Craft and Structure',
  'Cross-Text Connections': 'Craft and Structure',
  // Reading & Writing — Expression of Ideas
  'Rhetorical Synthesis': 'Expression of Ideas',
  Transitions: 'Expression of Ideas',
  // Reading & Writing — Standard English Conventions
  Boundaries: 'Standard English Conventions',
  'Form, Structure, and Sense': 'Standard English Conventions',

  // Math — Algebra
  'Linear equations in one variable': 'Algebra',
  'Linear equations in two variables': 'Algebra',
  'Linear functions': 'Algebra',
  'Linear inequalities': 'Algebra',
  'Linear inequalities in one or two variables': 'Algebra',
  'Systems of two linear equations': 'Algebra',
  'Systems of two linear equations in two variables': 'Algebra',
  // Math — Advanced Math
  'Equivalent expressions': 'Advanced Math',
  'Nonlinear equations in one variable and systems of equations in two variables': 'Advanced Math',
  'Nonlinear functions': 'Advanced Math',
  // Math — Problem-Solving and Data Analysis
  'Ratios and percentages': 'Problem-Solving and Data Analysis',
  'Ratios, rates, proportional relationships, and units': 'Problem-Solving and Data Analysis',
  Percentages: 'Problem-Solving and Data Analysis',
  'One-variable data (statistics)': 'Problem-Solving and Data Analysis',
  'Statistics and probability': 'Problem-Solving and Data Analysis',
  'Probability and conditional probability': 'Problem-Solving and Data Analysis',
  'Two-variable data': 'Problem-Solving and Data Analysis',
  'Scatterplots and margin of error': 'Problem-Solving and Data Analysis',
  'Inference from sample statistics and margin of error': 'Problem-Solving and Data Analysis',
  'Evaluating statistical claims': 'Problem-Solving and Data Analysis',
  // Math — Geometry and Trigonometry
  'Lines, angles, and triangles': 'Geometry and Trigonometry',
  'Area and volume': 'Geometry and Trigonometry',
  'Area, volume, and triangles': 'Geometry and Trigonometry',
  Circles: 'Geometry and Trigonometry',
  'Circles, right triangles, and trigonometry': 'Geometry and Trigonometry',
  'Right triangles and trigonometry': 'Geometry and Trigonometry',
}

/**
 * Map a topic string (anything stored in Question.topic) to its parent
 * Digital SAT domain.
 *
 * Resolves in priority order:
 *   1. Canonical taxonomy (src/lib/sat/taxonomy.ts) — the post-#368
 *      Title Case names that ~all active questions now use.
 *   2. Legacy TOPIC_TO_DOMAIN map — pre-migration topic strings
 *      (lowercase descriptive, archived umbrellas, etc.).
 *   3. "Other" — true unknowns. Surface as a catch-all rather than
 *      silently dropping data.
 *
 * Before the canonical taxonomy fallback was wired in (#382), this
 * function returned "Other" for every canonical Title Case topic
 * because the legacy map only knew lowercase legacy names. That broke
 * the admin Per-Domain Breakdown ("Algebra: 2 questions, Other:
 * 1,206"), score projection, topic leverage, and the study plan's
 * domain overview.
 */
export function domainForTopic(topic: string): SatDomain {
  // Canonical taxonomy lookup — returns null when the topic isn't
  // recognized as one of the 30 canonical Title Case names.
  const canonical = canonicalDomainForTopic(topic)
  if (canonical) return canonical
  return TOPIC_TO_DOMAIN[topic] ?? 'Other'
}

/**
 * Approximate number of questions per full SAT that come from a single
 * TOPIC (not its whole domain). Used as the frequency multiplier in
 * leverage scoring.
 *
 * We split the domain's per-test question count evenly across the domain's
 * topics: `DOMAIN_QUESTIONS_PER_TEST[domain] / CANONICAL_TOPIC_COUNT[domain]`.
 *
 * Why not the whole-domain count? Returning the full domain count for every
 * topic in the domain implicitly assumes mastering ONE topic captures all of
 * its siblings' test-day questions (1:1 skill transfer across the domain),
 * which over-credits topics in many-subtopic domains. Leverage answers "how
 * many test-day points do I gain by closing THIS topic's gap?", and that's
 * proportional to how many test questions are THIS topic — best estimated, in
 * the absence of a per-topic blueprint, by an even split (the same uniform
 * split the question generator commits to in `distributionForTotal`). This
 * only rescales the leverage multiplier; within a domain the divisor is
 * constant so within-domain ordering is unchanged. See backlog #14 / #897.
 *
 * Returns 0 for unmapped topics ("Other") so they sort to the bottom of any
 * leverage-ranked list rather than being treated as "average importance."
 */
export function questionsPerTestForTopic(topic: string): number {
  const domain = domainForTopic(topic)
  const domainTotal = DOMAIN_QUESTIONS_PER_TEST[domain]
  if (domainTotal === 0) return 0 // "Other"/unmapped — stays 0
  const topicCount = CANONICAL_TOPIC_COUNT[domain]
  return topicCount > 0 ? domainTotal / topicCount : 0
}

// ---------------------------------------------------------------------
// Topic-string canonicalization
// ---------------------------------------------------------------------
//
// The seed bank has a few near-duplicate topic strings (different batches
// used slightly different names). They map to the same domain, but on the
// per-topic heatmap they show up as separate cells. The canonicalTopic()
// helper merges them at display time so users see one cell per concept
// without needing a DB migration.
//
// Mapping target = the longer / more specific name (which appears more
// often in the bank); legacy short names map up.

const TOPIC_CANONICAL_MAP: Record<string, string> = {
  // Algebra
  'Linear inequalities': 'Linear inequalities in one or two variables',
  'Systems of two linear equations': 'Systems of two linear equations in two variables',
  // Geometry & Trig
  'Area and volume': 'Area, volume, and triangles',
  Circles: 'Circles, right triangles, and trigonometry',
  'Right triangles and trigonometry': 'Circles, right triangles, and trigonometry',
}

/**
 * Canonicalize a topic string for display. Returns the input unchanged
 * if it isn't in the duplicate-merge map.
 *
 * Use this on the display layer (heatmap, rationale text), NOT in queries
 * against the DB — Question.topic still stores the original string.
 */
export function canonicalTopic(topic: string): string {
  return TOPIC_CANONICAL_MAP[topic] ?? topic
}

// ---------------------------------------------------------------------
// Topic mastery tiers (Bronze / Silver / Gold)
// ---------------------------------------------------------------------
//
// Topic-level progression that mirrors the leverage / projection ceiling.
// Used by the topic heatmap to color cells with a metallic gradient
// matching the user's mastery state, and (later) in any "you mastered
// this topic" notification.
//
// Thresholds (per spec):
//   - "gold":   accuracy >= 85% with >= 10 attempts
//   - "silver": accuracy >= 70% with >= 10 attempts
//   - "bronze": accuracy >= 40% with >= 10 attempts
//   - "in_progress": >= 10 attempts but accuracy below 40%
//   - "untouched":   < 10 attempts (low-sample, no signal yet)

export type MasteryTier = 'untouched' | 'in_progress' | 'bronze' | 'silver' | 'gold'

/** Min attempts before a mastery tier other than "untouched" can apply. */
export const MASTERY_MIN_ATTEMPTS = 10

/** Accuracy thresholds (percentage 0-100). */
export const MASTERY_THRESHOLDS = {
  bronze: 40,
  silver: 70,
  gold: 85,
} as const

/**
 * Classify a topic's accuracy + sample size into a mastery tier.
 *
 * Untouched (sample < MASTERY_MIN_ATTEMPTS) is the gating state: a
 * topic with one lucky correct answer (100% over 1 attempt) should NOT
 * read as "Gold" — the sample is too small to mean anything. We require
 * 10 attempts before tagging mastery at all.
 */
export function masteryTier(accuracyPct: number, totalAttempted: number): MasteryTier {
  if (totalAttempted < MASTERY_MIN_ATTEMPTS) return 'untouched'
  if (accuracyPct >= MASTERY_THRESHOLDS.gold) return 'gold'
  if (accuracyPct >= MASTERY_THRESHOLDS.silver) return 'silver'
  if (accuracyPct >= MASTERY_THRESHOLDS.bronze) return 'bronze'
  return 'in_progress'
}

/**
 * Display metadata for each mastery tier — keeps the visual mapping
 * in one place so the heatmap, future badges, and tooltips can all
 * pull from the same source.
 *
 * cellClass: Tailwind classes applied to the heatmap cell.
 * label: short text shown in the cell when accuracy isn't meaningful
 *        (e.g., "—" for untouched).
 *
 * The per-tier medal GLYPH is no longer here — it moved to lucide icons in
 * TIER_ICON (src/lib/scoring/mastery-display.ts), rendered via <TierIcon>/
 * <MasteryMedal>, so the old emoji field was retired.
 */
export const MASTERY_DISPLAY: Record<MasteryTier, { cellClass: string; label: string }> = {
  untouched: {
    cellClass: 'border-slate-700 bg-slate-900/40 text-slate-400',
    label: 'Untouched',
  },
  in_progress: {
    cellClass: 'border-rose-500/40 bg-rose-900/40 text-rose-200',
    label: 'In progress',
  },
  bronze: {
    cellClass:
      'border-amber-700/50 bg-gradient-to-br from-amber-900/50 to-amber-950/40 text-amber-200',
    label: 'Bronze',
  },
  silver: {
    cellClass:
      'border-slate-400/40 bg-gradient-to-br from-slate-500/30 to-slate-700/40 text-slate-100',
    label: 'Silver',
  },
  gold: {
    cellClass:
      'border-yellow-500/60 bg-gradient-to-br from-yellow-700/50 to-amber-900/50 text-yellow-100 shadow-[0_0_18px_-6px_rgba(250,204,21,0.5)]',
    label: 'Gold',
  },
}

/**
 * Human label for a mastery tier, aware of how much data backs it. The
 * `untouched` tier covers BOTH "never answered" AND "answered a few times but
 * still under MASTERY_MIN_ATTEMPTS (10) — not enough to rate". Calling the
 * latter "Untouched" reads as "you haven't done this" when the student clearly
 * has (the Leverage Board shows topics with 2-3 attempts). So split it: a topic
 * with attempts is "Just started", a truly untouched one is "Not started".
 * All other tiers keep their MASTERY_DISPLAY label.
 */
export function masteryTierLabel(tier: MasteryTier, totalAttempted: number): string {
  if (tier === 'untouched') return totalAttempted > 0 ? 'Just started' : 'Not started'
  return MASTERY_DISPLAY[tier].label
}
